import React from 'react';
import { Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import dayjs from 'utils/dayjs';
import useInboxConversations from 'hooks/inbox/useInboxConversations';
import useInboxAllUnreadCount from 'hooks/inbox/useInboxAllUnreadCount';
import { EMessageSenderType, TConversation } from 'types/inbox';
import ClockIcon from '../TodayAppointments/icons/ClockIcon';
import s from './s.module.less';

const UnreadMessages: React.FC = () => {
    const navigate = useNavigate();
    const [conversations, loading] = useInboxConversations(50);
    const [allUnreadCount] = useInboxAllUnreadCount();

    const handleReply = (conversation: TConversation) => {
        const patientMember = conversation.members?.find(
            (member) => member.userType === EMessageSenderType.PATIENT,
        );
        const clientId = patientMember?.userId;
        const baseUrl = `${PATH.DASHBOARD}/${PATH.INBOX}?conversationId=${conversation.conversationId}`;
        const url = clientId ? `${baseUrl}&clientId=${clientId}` : baseUrl;
        window.open(url, '_blank');
    };

    const handleViewAll = () => {
        navigate(`${PATH.DASHBOARD}/${PATH.INBOX}`);
    };

    // 获取未读消息的对话列表
    const unreadConversations = React.useMemo(() => {
        if (!conversations) return [];
        return conversations
            .filter((conv: TConversation) => conv.unreadCount > 0)
            .sort((a: TConversation, b: TConversation) => {
                // 按最后消息时间排序，最新的在前
                return new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime();
            })
            .slice(0, 2); // 只取前2条
    }, [conversations]);

    const formatTime = (timeStr: string) => {
        const messageTime = dayjs(timeStr);
        const now = dayjs();
        const today = now.startOf('day');
        const messageDate = messageTime.startOf('day');

        if (messageDate.isSame(today, 'day')) {
            // 今天：显示 "Today, Mar 13, 05:30 PM"
            return `Today, ${messageTime.format('MMM D, hh:mm A')}`;
        } else {
            // 其他日期：显示 "Mar 10, 09:00 PM"
            return messageTime.format('MMM D, hh:mm A');
        }
    };

    const getPatientName = (conversation: TConversation) => {
        const patientMember = conversation.members?.find(
            (member) => member.userType === EMessageSenderType.PATIENT,
        );

        if (patientMember) {
            if (patientMember.fullName) {
                return patientMember.fullName;
            }
            const name = `${patientMember.firstName || ''} ${patientMember.lastName || ''}`.trim();
            if (name) {
                return name;
            }
        }

        return 'Unknown';
    };

    if (loading || !allUnreadCount || allUnreadCount === 0) {
        return null;
    }

    return (
        <div className={s.wrapper}>
            <h3 className={s.title}>Unread messages ({allUnreadCount})</h3>
            <div className={s.card}>
                <div className={s.content}>
                    {unreadConversations.length > 0 ? (
                        <>
                            {unreadConversations.map((conversation, index) => (
                                <React.Fragment
                                    key={conversation.conversationId}
                                >
                                    <div className={s.messageItem}>
                                        <div className={s.leftSection}>
                                            <div className={s.messageHeader}>
                                                <div className={s.patientInfo}>
                                                    <span
                                                        className={
                                                            s.patientName
                                                        }
                                                    >
                                                        {getPatientName(
                                                            conversation,
                                                        )}
                                                    </span>
                                                </div>
                                                <Button
                                                    className={s.replyButton}
                                                    onClick={() =>
                                                        handleReply(conversation)
                                                    }
                                                >
                                                    Reply
                                                </Button>
                                            </div>
                                            <div className={s.messagePreview}>
                                                {conversation.lastMessageContent ||
                                                    'No message'}
                                            </div>
                                            <div className={s.messageTime}>
                                                <ClockIcon />
                                                <span>
                                                    {formatTime(
                                                        conversation.lastMessageAt,
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    {index < unreadConversations.length - 1 && (
                                        <div className={s.divider} />
                                    )}
                                </React.Fragment>
                            ))}
                            <div className={s.viewAll} onClick={handleViewAll}>
                                <span className={s.viewAllText}>View all</span>
                                <div className={s.arrowIcon}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M12.2929 5.29289C12.6834 4.90237 13.3166 4.90237 13.7071 5.29289L17.7071 9.29289C18.0976 9.68342 18.0976 10.3166 17.7071 10.7071L13.7071 14.7071C13.3166 15.0976 12.6834 15.0976 12.2929 14.7071C11.9024 14.3166 11.9024 13.6834 12.2929 13.2929L14.5858 11H3C2.44772 11 2 10.5523 2 10C2 9.44772 2.44772 9 3 9H14.5858L12.2929 6.70711C11.9024 6.31658 11.9024 5.68342 12.2929 5.29289Z"
                                            fill="#0E62F4"
                                        />
                                    </svg>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className={s.empty}>No unread messages</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UnreadMessages;
